import { isDeepEqual } from 'remeda';

import { type UnityCompare } from '@@api/services/content/schemas/article';

import { type RootElement } from './groups';

import { RichTextCodec } from './index';

export type PlateCompareElementOrNull = RootElement | null | undefined;

export type PlateCompare = {
    originArticle: {
        revisionSource: string;
        revision: string | null;
        content: PlateCompareElementOrNull[];
    };
    derivativeArticle: {
        revisionSource: string;
        revision: string | null;
        contentModifications: PlateCompareOperation[];
    };
};

export type PlateCompareOperation =
    | { operation: 'IDENTITY' }
    | { operation: 'REMOVE' }
    | { operation: 'ADD'; element: RootElement }
    | { operation: 'UPDATE'; element: RootElement }
    | { operation: 'MOVE_START'; to: number }
    | { operation: 'MOVE_END'; from: number }
    | { operation: 'MOVE_END'; from: number; updated: true; element: RootElement };

/**
 * Recursively strips `id` fields from a node tree. Used for content comparison
 * because Plate codecs regenerate child element IDs during serialization round-trips,
 * causing `isDeepEqual` to see identical content as different.
 */
const stripIds = (node: unknown): unknown => {
    if (Array.isArray(node)) {
        return node.map(stripIds);
    }

    if (node !== null && typeof node === 'object') {
        const entries = Object.entries(node)
            .filter(([key]) => key !== 'id')
            .map(([key, value]) => [key, stripIds(value)]);

        return Object.fromEntries(entries);
    }

    return node;
};

const decodeElement = (element: unknown): RootElement | null => {
    const result = RichTextCodec.safeDecode({
        richTextFormat: [element],
    } as Parameters<typeof RichTextCodec.safeDecode>[0]);

    if (!result.success) {
        console.warn('Failed to decode compare element with Plate.js codec', result.error, element);

        return null;
    }

    return result.data[0] as RootElement;
};

export const decodeCompare = (payload: UnityCompare): PlateCompare => {
    // Decode origin content first so we can compare against modifications
    // to detect false UPDATEs caused by Plate serialization round-trip differences.
    const decodedOriginContent = payload.originArticle.content.richTextFormat.map((element) =>
        element ? decodeElement(element) : null,
    );

    return {
        originArticle: {
            ...payload.originArticle,
            content: decodedOriginContent,
        },
        derivativeArticle: {
            ...payload.derivativeArticle,
            contentModifications: payload.derivativeArticle.contentModifications.map(
                (modification, index): PlateCompareOperation => {
                    if ('element' in modification) {
                        const element = decodeElement(modification.element);

                        if (element) {
                            // When Plate serializes content back to Unity format, minor differences
                            // (e.g. omitted defaults, attribute ordering) can cause the server to
                            // detect unchanged blocks as UPDATE. After decoding both sides through
                            // the same codec, genuinely identical content produces identical Plate
                            // nodes — downgrade these false UPDATEs to IDENTITY.
                            if (
                                modification.operation === 'UPDATE' &&
                                decodedOriginContent[index]
                            ) {
                                // Normalize for comparison:
                                // 1. JSON round-trip strips `undefined` values
                                // 2. stripIds removes child element IDs that Plate
                                //    codecs regenerate during serialization round-trips
                                const normalizedOrigin = stripIds(
                                    JSON.parse(JSON.stringify(decodedOriginContent[index])),
                                );
                                const normalizedElement = stripIds(
                                    JSON.parse(JSON.stringify(element)),
                                );

                                if (isDeepEqual(normalizedOrigin, normalizedElement)) {
                                    return { operation: 'IDENTITY' };
                                }
                            }

                            return { ...modification, element };
                        }

                        // Element type not supported by Plate codec — fall back to IDENTITY
                        return { operation: 'IDENTITY' };
                    }

                    return modification;
                },
            ),
        },
    };
};
